import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@rsos/components/capstone/base';
import { selectAppLauncherApp } from '@rsos/reduxDataStore/src/features/appLauncher/appLauncherSlice';
import { EllingtonCard } from '../../AdditionalData/EllingtonCard/EllingtonCard';
import AviveCancelModal from './AviveCancelModal';
import AviveStatus from './AviveStatus';
import {
  cancelSearch,
  normalizePhone,
  resetAviveNotes,
  restoreAviveNotes,
  toggleNotesVisibility,
} from './aviveSlice';
import {
  AED_STATUS_MODE,
  AVIVE2_STATUS_CANCELING,
  AVIVE2_STATUS_DEVICE_ACTIVATED,
  AVIVE2_STATUS_EMERGENCY_TIMEOUT,
  AVIVE2_STATUS_NOTE_SENT,
  AVIVE2_STATUS_UNABLE_TO_SEND_NOTE,
  findAviveStatus,
} from './aviveStatusTypes';

/**
 * AviveStatuses shows the status of the ongoing search.
 * The search is identified by the phone_number associated with it.
 * The 2 types of searches are supported by this same component:
 *   1. A search when a call is selected.
 *   2. A search when no call is selected.
 * The difference is that #1 presents the AviveStatuses directly in the pane.
 * The AviveStatuses for #2 is presented in a MapCard and opened via map marker.
 * All Avive searches are plotted through the avive_aed_request layer.
 */
const AviveStatuses = ({ phone = '', p = 0, pb = 0 }) => {
  const dispatch = useDispatch();

  const [isAviveModalOpen, setIsAviveModalOpen] = useState(false);

  // The currently selected call.
  const callerID = useSelector(state => state.emergencyCalls.selectedCallerID);

  // When the user is doing a standalone AED search, the prop `phone` will be
  // the phone number we should look up the status for. Otherwise, they're
  // seeing this component because they've selected a call from the queue, in
  // which case use callerID.
  const targetPhone = normalizePhone(phone) || callerID;

  // Get the latest status details for the targetPhone.
  const aviveStatus = useSelector(state => {
    return state.avive.statuses[`tel://${targetPhone}`];
  });

  const isCanceling = useSelector(state => {
    return state.avive.statuses[`tel://${targetPhone}`]?.states?.isCanceling;
  });

  const isCanceled = useSelector(state => {
    return state.avive.statuses[`tel://${targetPhone}`]?.states?.isCanceled;
  });

  const isTimeout = useSelector(state => {
    return state.avive.statuses[`tel://${targetPhone}`]?.states?.isTimeout;
  });

  const isNotesSent = useSelector(state => {
    return state.avive.statuses[`tel://${targetPhone}`]?.states?.isNotesSent;
  });

  const isNotesSending = useSelector(state => {
    return state.avive?.statuses[`tel://${targetPhone}`]?.states
      ?.isNotesSending;
  });

  const isNotesError = useSelector(state => {
    return state.avive.statuses[`tel://${targetPhone}`]?.states?.isNotesError;
  });

  useEffect(() => {
    if (!isCanceling && !isCanceled && !isTimeout) {
      window.__RSOS?.hermes?.instance?.subscribe(`tel://${targetPhone}`);
    }
  }, [isCanceling, isCanceled, isTimeout, targetPhone]);

  return (
    <Box p={p} pb={pb}>
      {isCanceling && (
        <AviveStatus
          status={{
            message: AVIVE2_STATUS_CANCELING,
            mode: AED_STATUS_MODE.STANDALONE,
          }}
        />
      )}

      {isNotesError && (
        <AviveStatus
          status={findAviveStatus(AVIVE2_STATUS_UNABLE_TO_SEND_NOTE)}
          onClose={() => {
            dispatch(restoreAviveNotes());
          }}
        />
      )}

      {isNotesSent && (
        <>
          <AviveStatus
            status={{
              message: AVIVE2_STATUS_NOTE_SENT,
              mode: AED_STATUS_MODE.STANDALONE,
            }}
            onClose={() => {
              dispatch(resetAviveNotes());
            }}
          />
        </>
      )}

      {isTimeout && (
        <>
          <AviveStatus
            status={findAviveStatus(AVIVE2_STATUS_EMERGENCY_TIMEOUT)}
            onClose={() => {
              dispatch(resetAviveNotes());
            }}
          />
        </>
      )}

      {!isCanceling && (
        <>
          <AviveStatus
            status={aviveStatus?.status}
            isNotesSending={isNotesSending}
            onNewNote={() => {
              dispatch(selectAppLauncherApp({ appName: 'avive' }));
              dispatch(toggleNotesVisibility({ phone: targetPhone }));
            }}
            onCancel={() => {
              setIsAviveModalOpen(true);
            }}
            onCancelRetry={() => {
              dispatch(
                cancelSearch({
                  phone: targetPhone,
                }),
              );
            }}
          />

          {/* Only show the EllingtonCard if used in standalone mode. */}
          {phone &&
            aviveStatus?.data?.emergency_status_update ===
              AVIVE2_STATUS_DEVICE_ACTIVATED && (
              <EllingtonCard
                data={aviveStatus?.data}
                display_schema={aviveStatus?.displaySchema}
                createdTime={aviveStatus?.createdTime}
              />
            )}
        </>
      )}

      <AviveCancelModal
        isOpened={isAviveModalOpen}
        onClose={() => {
          setIsAviveModalOpen(false);
        }}
        onConfirm={async () => {
          setIsAviveModalOpen(false);
          await dispatch(
            cancelSearch({
              phone: targetPhone,
            }),
          );
        }}
      />
    </Box>
  );
};

export default AviveStatuses;
